import React, { useState } from 'react';
import {
  Search,
  FileText,
  HelpCircle,
  ChevronDown,
  ChevronRight,
  MessageCircle,
  Phone,
  Mail,
  BookOpen,
  Video,
  Users,
  Star,
  ThumbsUp,
  ThumbsDown,
  PawPrint,
  Shield,
  Zap,
  Clock,
  CheckCircle,
  AlertCircle,
  Info
} from 'lucide-react';

const HelpCenter = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('getting-started');

  const categories = [
    {
      id: 'getting-started',
      name: 'Getting Started',
      icon: BookOpen,
      color: 'bg-blue-100 text-blue-600',
      articles: 12
    },
    {
      id: 'health-records',
      name: 'Health Records',
      icon: FileText,
      color: 'bg-emerald-100 text-emerald-600',
      articles: 8
    },
    {
      id: 'reminders',
      name: 'Reminders & Alerts',
      icon: Clock,
      color: 'bg-yellow-100 text-yellow-600',
      articles: 6
    },
    {
      id: 'account',
      name: 'Account Management',
      icon: Users,
      color: 'bg-purple-100 text-purple-600',
      articles: 5
    },
    {
      id: 'troubleshooting',
      name: 'Troubleshooting',
      icon: AlertCircle,
      color: 'bg-red-100 text-red-600',
      articles: 10
    },
    {
      id: 'mobile-app',
      name: 'Mobile App',
      icon: Phone,
      color: 'bg-indigo-100 text-indigo-600',
      articles: 7
    }
  ];

  const faqs = [
    {
      id: 1,
      question: 'How do I add a new pet to my account?',
      answer: 'To add a new pet, go to the Overview tab and click the "+" button in the top right corner. Fill in your pet\'s basic information including name, species, breed, age, and other details. You can also upload a photo of your pet.',
      category: 'getting-started',
      popularity: 95
    },
    {
      id: 2,
      question: 'How do I upload health records and documents?',
      answer: 'Click on "Add Record" in the main navigation, select the record type (vaccination, checkup, etc.), fill in the details, and use the file upload area to attach documents. Supported formats include PDF, JPG, PNG up to 10MB each.',
      category: 'health-records',
      popularity: 89
    },
    {
      id: 3,
      question: 'Can I set up automatic reminders for vaccinations?',
      answer: 'Yes! Go to the Reminders tab and click "Set Reminder" for any upcoming vaccination or appointment. You can choose to receive notifications via email, SMS, or in-app notifications.',
      category: 'reminders',
      popularity: 87
    },
    {
      id: 4,
      question: 'How do I share health records with my veterinarian?',
      answer: 'Each health record has a share button. Click it to generate a secure link that you can send to your veterinarian. You can also export records as PDF files for easy sharing.',
      category: 'health-records',
      popularity: 82
    },
    {
      id: 5,
      question: 'Is my pet\'s data secure and private?',
      answer: 'Absolutely! We use bank-level encryption to protect your data. All information is stored securely in the cloud with regular backups. We never share your pet\'s information with third parties without your explicit consent.',
      category: 'account',
      popularity: 91
    },
    {
      id: 6,
      question: 'Can I access my pet\'s records offline?',
      answer: 'While the app requires an internet connection for syncing, you can download individual records as PDF files for offline access. The mobile app also caches recent records for limited offline viewing.',
      category: 'mobile-app',
      popularity: 78
    },
    {
      id: 7,
      question: 'What should I do if I accidentally deleted a record?',
      answer: 'Don\'t worry! Deleted records are moved to a "Trash" folder where they\'re kept for 30 days before permanent deletion. Go to Settings > Trash to restore accidentally deleted records.',
      category: 'troubleshooting',
      popularity: 74
    },
    {
      id: 8,
      question: 'How do I change my notification preferences?',
      answer: 'Go to Settings > Notifications to customize when and how you receive alerts. You can set different preferences for vaccination reminders, appointment alerts, and health updates.',
      category: 'account',
      popularity: 69
    }
  ];

  const quickLinks = [
    {
      title: 'Video Tutorials',
      description: 'Watch step-by-step guides',
      icon: Video,
      color: 'bg-red-100 text-red-600'
    },
    {
      title: 'Live Chat Support',
      description: 'Get instant help from our team',
      icon: MessageCircle,
      color: 'bg-green-100 text-green-600'
    },
    {
      title: 'Phone Support',
      description: 'Speak directly with support',
      icon: Phone,
      color: 'bg-blue-100 text-blue-600'
    },
    {
      title: 'Email Support',
      description: 'Send us a detailed message',
      icon: Mail,
      color: 'bg-purple-100 text-purple-600'
    }
  ];

  const filteredFaqs = faqs.filter(faq => 
    (selectedCategory === 'all' || faq.category === selectedCategory) &&
    (faq.question.toLowerCase().includes(searchTerm.toLowerCase()) || 
     faq.answer.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const toggleFaq = (id) => {
    setExpandedFaq(expandedFaq === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-xl flex items-center justify-center">
                <HelpCircle className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Help Center</h1>
                <p className="text-gray-600">Find answers to common questions and get support</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 bg-emerald-100 px-4 py-2 rounded-full">
                <Shield className="w-5 h-5 text-emerald-600" />
                <span className="text-emerald-700 font-medium">24/7 Support</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Search Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-2xl shadow-sm p-8 mb-8">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">How can we help you?</h2>
            <p className="text-gray-600">Search our knowledge base or browse by category</p>
          </div>
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search for help articles, guides, or FAQs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-lg"
              />
            </div>
          </div>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {quickLinks.map((link, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-sm p-6 hover:shadow-md transition-shadow cursor-pointer">
              <div className="flex items-center space-x-4">
                <div className={`w-12 h-12 ${link.color} rounded-lg flex items-center justify-center`}>
                  <link.icon className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{link.title}</h3>
                  <p className="text-sm text-gray-600">{link.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Categories Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-sm p-6 sticky top-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Categories</h3>
              <div className="space-y-2">
                <button
                  onClick={() => setSelectedCategory('all')}
                  className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                    selectedCategory === 'all' ? 'bg-emerald-100 text-emerald-700' : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <FileText className="w-5 h-5" />
                    <span>All Articles</span>
                  </div>
                  <span className="text-sm bg-gray-100 px-2 py-1 rounded">
                    {faqs.length}
                  </span>
                </button>
                {categories.map(category => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                      selectedCategory === category.id ? 'bg-emerald-100 text-emerald-700' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-8 h-8 ${category.color} rounded-lg flex items-center justify-center`}>
                        <category.icon className="w-4 h-4" />
                      </div>
                      <span>{category.name}</span>
                    </div>
                    <span className="text-sm bg-gray-100 px-2 py-1 rounded">
                      {category.articles}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* FAQ Content */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900">
                  Frequently Asked Questions
                </h3>
                <span className="text-sm text-gray-500">
                  {filteredFaqs.length} articles found
                </span>
              </div>
              
              <div className="space-y-4">
                {filteredFaqs.map(faq => (
                  <div key={faq.id} className="border border-gray-200 rounded-lg">
                    <button
                      onClick={() => toggleFaq(faq.id)}
                      className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <span className="font-medium text-gray-900">{faq.question}</span>
                        <div className="flex items-center space-x-2">
                          <Star className="w-4 h-4 text-yellow-400 fill-current" />
                          <span className="text-sm text-gray-500">{faq.popularity}% helpful</span>
                        </div>
                      </div>
                      {expandedFaq === faq.id ? (
                        <ChevronDown className="w-5 h-5 text-gray-500" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-500" />
                      )}
                    </button>
                    {expandedFaq === faq.id && (
                      <div className="px-4 pb-4">
                        <div className="border-t pt-4">
                          <p className="text-gray-700 mb-4">{faq.answer}</p>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                              <span className="text-sm text-gray-500">Was this helpful?</span>
                              <div className="flex items-center space-x-2">
                                <button className="flex items-center space-x-1 text-green-600 hover:text-green-700">
                                  <ThumbsUp className="w-4 h-4" />
                                  <span className="text-sm">Yes</span>
                                </button>
                                <button className="flex items-center space-x-1 text-red-600 hover:text-red-700">
                                  <ThumbsDown className="w-4 h-4" />
                                  <span className="text-sm">No</span>
                                </button>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2 text-sm text-gray-500">
                              <CheckCircle className="w-4 h-4" />
                              <span>Updated recently</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Contact Support */}
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl shadow-sm p-8 mt-8 text-white">
              <div className="text-center">
                <MessageCircle className="w-12 h-12 mx-auto mb-4 opacity-90" />
                <h3 className="text-2xl font-bold mb-2">Still need help?</h3>
                <p className="text-emerald-100 mb-6">
                  Can't find what you're looking for? Our support team is here to help you 24/7.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button className="bg-white text-emerald-600 px-6 py-3 rounded-lg font-semibold hover:bg-emerald-50 transition-colors">
                    Start Live Chat
                  </button>
                  <button className="border border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-white hover:text-emerald-600 transition-colors">
                    Email Support
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpCenter;